import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, Loader2, Copy, Mail, MessageCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLE = { sent: "bg-stone-100 text-stone-600", opened: "bg-blue-50 text-blue-700", registered: "bg-amber-50 text-amber-700", paid: "bg-green-50 text-green-700" };

const statusLabel = (inv) => {
  if (inv.status === "paid") return "paid";
  if (inv.registration_status === "payment_failed") return "payment failed";
  if (inv.status === "registered") return "registered, awaiting payment";
  return inv.status;
};

const SendLinkDialog = ({ open, onClose, token, onSent }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(null);
  const send = async (via) => {
    setBusy(via);
    try {
      const r = await fetch(`${API}/staff/registration-links`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ ...f, send_via: via }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Please check the details."); return; }
      await navigator.clipboard.writeText(d.url).catch(() => {});
      if (via === "whatsapp") {
        const phone = (f.phone || "").replace(/[^0-9]/g, "");
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(d.message)}`, "_blank");
        toast.success("WhatsApp opened with the message. Link also copied.");
      } else if (via === "email") toast[d.emailed ? "success" : "error"](d.emailed ? `Link emailed to ${f.email}. Also copied.` : "Email could not be sent. The link is copied instead.");
      else toast.success("Registration link copied.");
      onSent(); onClose(); setF({ name: "", email: "", phone: "" });
    } catch { toast.error("Connection error."); } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="send-registration-dialog">
        <DialogHeader><DialogTitle>Send programme registration link</DialogTitle></DialogHeader>
        <p className="text-xs text-stone-500 -mt-2">The link opens the registration form directly, then Paystack checkout. No health details are included in the link or message.</p>
        <div className="space-y-3">
          <Input placeholder="Full name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="invite-name" />
          <Input placeholder="Email (to send by email)" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="invite-email" />
          <Input placeholder="WhatsApp number with country code (+234...)" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="invite-phone" />
        </div>
        <div className="grid grid-cols-3 gap-2 pt-2">
          <Button variant="outline" onClick={() => send("copy")} disabled={!!busy} data-testid="invite-copy-btn">{busy === "copy" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}Copy</Button>
          <Button variant="outline" onClick={() => send("email")} disabled={!!busy || !f.email} data-testid="invite-email-btn">{busy === "email" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}Email</Button>
          <Button onClick={() => send("whatsapp")} disabled={!!busy || !f.phone} className="bg-green-600 hover:bg-green-700 text-white" data-testid="invite-whatsapp-btn">{busy === "whatsapp" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />}WhatsApp</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RegistrationLinks = ({ token }) => {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    fetch(`${API}/staff/registration-links`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setInvites(d.invites || [])).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const directUrl = `${process.env.REACT_APP_BACKEND_URL}/api/s/programme/register`;
  return (
    <Card className="rounded-xl border-stone-200/60 p-5 space-y-4" data-testid="registration-links-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-stone-900 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#FF7A00]" /> Programme registration links</p>
          <p className="text-xs text-stone-500 mt-0.5">For people you've spoken with who are ready to enrol.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => { navigator.clipboard.writeText(directUrl); toast.success("General registration link copied."); }} data-testid="copy-direct-link-btn"><Copy className="w-3 h-3 mr-1" />Copy general link</Button>
          <Button size="sm" className="text-xs bg-[#FF7A00] hover:bg-[#E56E00] text-white" onClick={() => setOpen(true)} data-testid="send-registration-link-btn"><Send className="w-3 h-3 mr-1" />Send Programme Registration Link</Button>
        </div>
      </div>
      {invites.length > 0 && (
        <div className="divide-y divide-stone-100" data-testid="registration-links-list">
          {invites.slice(0, 15).map((inv, i) => (
            <div key={i} className="flex items-center justify-between py-2 text-sm">
              <div>
                <span className="font-medium text-stone-800">{inv.name}</span>
                <span className="text-xs text-stone-400 ml-2">via {inv.sent_via} by {inv.sent_by_name || "staff"}, {new Date(inv.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
              </div>
              <Badge className={`text-xs capitalize ${STATUS_STYLE[inv.status] || ""}`} data-testid={`invite-status-${i}`}>{statusLabel(inv)}</Badge>
            </div>
          ))}
        </div>
      )}
      <SendLinkDialog open={open} onClose={() => setOpen(false)} token={token} onSent={load} />
    </Card>
  );
};
